import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import POSPage from "./POSPage";
import { Loader2 } from "lucide-react";

/**
 * POSTerminalPage - Página dedicada para o terminal POS em tablet
 * 
 * Esta página:
 * - Abre diretamente no modo POS sem sidebar
 * - Não mostra menu de navegação
 * - Ideal para instalação como PWA em tablets
 * - Uso: /pos-terminal
 */
export default function POSTerminalPage() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [ready, setReady] = useState(false);

  useEffect(() => {
    // Check if user is logged in and has permission to use POS
    if (!loading) {
      if (!user) {
        navigate("/login?redirect=/pos-terminal");
        return;
      }
      
      // Sirviente (atendente) can use POS
      // Gerente can use POS
      // Owner can use POS
      const canUsePOS = user.role === "sirviente" || 
                        user.role === "gerente" || 
                        user.role === "gerente_manager" ||
                        user.role === "owner" ||
                        !user.role; // Owner usually doesn't have role set
      
      if (!canUsePOS) {
        navigate("/dashboard");
        return;
      }
      
      setReady(true);
    }
  }, [user, loading, navigate]);

  // Configure viewport for tablet
  useEffect(() => {
    // Add meta viewport for tablet
    const existingMeta = document.querySelector('meta[name="viewport"]');
    if (existingMeta) {
      existingMeta.setAttribute('content', 'width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no');
    }
    
    // Prevent scroll bounce on iOS
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.width = '100%';
    document.body.style.height = '100%';
    
    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.height = '';
    };
  }, []);

  if (loading || !ready) {
    return (
      <div className="min-h-screen bg-[#1a1a1a] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-emerald-500 mx-auto mb-4" />
          <p className="text-white text-lg">Carregando Kaiso POS...</p>
        </div>
      </div>
    );
  }

  // Render POS page in terminal mode (full screen, no sidebar)
  return (
    <div className="min-h-screen bg-[#1a1a1a]" data-testid="pos-terminal">
      <POSPage isTerminalMode={true} />
    </div>
  );
}
